<template>
  <v-dialog :value="value" max-width="640px" persistent scrollable @input="$emit('input', $event)">
    <v-card class="ui-card deadline-dialog">
      <div class="ui-card__header deadline-dialog__header">
        <h3 class="ui-section-title">{{ request ? 'Alterar prazo desta solicitação' : 'Alterar prazo de documentação em lote' }}</h3>
      </div>
      <div class="ui-card__body deadline-dialog__body">
        <p class="ui-small ui-muted mb-4">
          O novo prazo vale até o fim do dia e reabre o envio das solicitações já vencidas.
          Documentos já enviados mantêm a marcação que tinham (no prazo ou fora do prazo).
        </p>
        <v-text-field v-model="deadline" type="date" :min="todayIso" label="Novo prazo" outlined dense hide-details="auto" class="mb-4" />

        <template v-if="request">
          <div class="deadline-dialog__single">
            <strong>{{ request.creditor_name || 'Solicitação geral da devedora' }}</strong>
            <div class="ui-small ui-muted">
              <span v-if="request.creditor_registration">{{ request.creditor_registration }} · </span>
              prazo atual {{ formatDate(request.deadline) }} · {{ statusLabel(request.status) }}
              <span v-if="request.target === 'creditor'"> · solicitada ao credor</span>
            </div>
            <div v-for="doc in request.documents" :key="`deadline-doc-${doc.id}`" class="d-flex align-center justify-space-between mt-2">
              <span><v-icon left small>mdi-file-document-outline</v-icon>{{ doc.name }}</span>
              <ui-badge v-if="doc.tag" :text="doc.tag" :color="doc.uploaded_after_deadline ? 'error' : (doc.file_url ? 'success' : 'warning')" />
            </div>
          </div>
        </template>

        <template v-else>
          <v-progress-linear v-if="loading" indeterminate class="mb-2" />
          <div v-else-if="!list.length" class="ui-small ui-muted">Nenhuma solicitação de documentos neste caso.</div>
          <template v-else>
            <div class="d-flex align-center justify-space-between mb-1">
              <strong>Solicitações ({{ selected.length }} de {{ list.length }})</strong>
              <v-btn x-small text color="primary" @click="toggleAll">{{ allSelected ? 'Desmarcar todas' : 'Marcar todas' }}</v-btn>
            </div>
            <div class="deadline-dialog__list">
              <v-checkbox v-for="item in list" :key="`deadline-${item.id}`" v-model="selected" :value="item.id" dense hide-details class="mt-0 mb-1">
                <template #label>
                  <span>
                    {{ item.creditor_name || 'Solicitação geral da devedora' }}
                    <span class="ui-muted"> · prazo atual {{ formatDate(item.deadline) }} · {{ statusLabel(item.status) }}<span v-if="item.target === 'creditor'"> · ao credor</span></span>
                  </span>
                </template>
              </v-checkbox>
            </div>
          </template>
        </template>

        <v-checkbox v-if="!request || request.target !== 'creditor'" v-model="notify"
          label="Avisar a devedora por e-mail sobre o novo prazo" dense hide-details class="mt-4" />
        <v-textarea v-if="notify" v-model="message" label="Mensagem para a devedora (opcional)" rows="2" auto-grow outlined dense hide-details class="mt-3" />
        <p v-if="notify && selectedCreditorTargets" class="ui-small ui-muted mt-2 mb-0">
          O aviso vai só para a devedora; {{ selectedCreditorTargets }} solicitação(ões) feita(s) ao credor não gera(m) e-mail.
        </p>
      </div>
      <div class="ui-card__footer deadline-dialog__footer">
        <v-btn text :disabled="saving" @click="$emit('input', false)">Cancelar</v-btn>
        <v-btn v-if="$allowed('client.document_requests')" color="primary" depressed :loading="saving" :disabled="!deadline || !selected.length" @click="save">Salvar novo prazo</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

const STATUS_LABELS = { 0: "Pendente", 1: "Enviado", 2: "Justificado", 3: "Concluído" };

// Prorrogacao do prazo das solicitacoes de documentos de um caso.
// Com `request`, altera so aquela solicitacao; sem, altera em lote. A lista do
// lote vem em `requests` (detalhe do caso) ou e carregada aqui (Central).
export default {
  name: "DocumentRequestDeadlineDialog",

  props: {
    value: { type: Boolean, default: false },
    clientId: { type: [Number, String], default: null },
    request: { type: Object, default: null },
    requests: { type: Array, default: null },
  },

  data: () => ({
    deadline: "",
    notify: true,
    message: "",
    selected: [],
    saving: false,
    loading: false,
    loadedRequests: [],
  }),

  computed: {
    list() {
      return this.requests || this.loadedRequests;
    },
    todayIso() {
      return this.$moment().format("YYYY-MM-DD");
    },
    allSelected() {
      return this.list.length > 0 && this.selected.length === this.list.length;
    },
    selectedCreditorTargets() {
      if (this.request) return 0;
      return this.list.filter((item) => item.target === "creditor" && this.selected.includes(item.id)).length;
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
    },
  },

  methods: {
    async reset() {
      this.deadline = "";
      this.message = "";
      this.saving = false;
      // Solicitacao feita ao credor nao tem aviso a devedora.
      this.notify = !this.request || this.request.target !== "creditor";
      if (this.request) {
        this.selected = [this.request.id];
        return;
      }
      if (!this.requests) await this.loadRequests();
      // No lote, ja marca as solicitacoes com documento ainda sem envio.
      const hasPending = (item) => (item.documents || []).some((doc) => !doc.file_url && !(doc.files && doc.files.length));
      this.selected = this.list.filter(hasPending).map((item) => item.id);
    },

    async loadRequests() {
      if (!this.clientId) return;
      this.loading = true;
      try {
        const result = await axios.get(`/api/admin/clients/${this.clientId}/document-requests`);
        this.loadedRequests = result.data.content || [];
      } catch (error) {
        this.loadedRequests = [];
        this.$dialog.notify.error(this.$codes(error) || "Erro ao carregar as solicitações", { position: "top-right" });
      } finally {
        this.loading = false;
      }
    },

    toggleAll() {
      this.selected = this.allSelected ? [] : this.list.map((item) => item.id);
    },

    async save() {
      if (!this.deadline || !this.selected.length || !this.clientId) return;
      this.saving = true;
      try {
        const result = await axios.patch(`/api/admin/clients/${this.clientId}/document-requests/deadline`, {
          request_ids: this.selected,
          deadline: this.deadline,
          notify: this.notify,
          message: this.message || null,
        });
        const content = result.data.content || {};
        const date = this.deadline.split("-").reverse().join("/");
        let text = `Prazo alterado para ${date} em ${content.updated || this.selected.length} solicitação(ões).`;
        if (this.notify) text += content.notified ? " A devedora foi avisada por e-mail." : " Não foi possível enviar o aviso por e-mail.";
        this.$dialog.notify.info(text, { position: "top-right", timeout: 4000 });
        this.$emit("saved", content);
        this.$emit("input", false);
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error) || "Erro ao alterar o prazo", { position: "top-right" });
      } finally {
        this.saving = false;
      }
    },

    formatDate(date) {
      if (!date || date === "0000-00-00") return "-";
      return this.$moment(date).utc(false).format("DD/MM/YYYY");
    },

    statusLabel(status) {
      return STATUS_LABELS[status] || "Desconhecido";
    },
  },
};
</script>

<style lang="scss" scoped>
/* Cabecalho e rodape fixos; so o corpo rola (mesmo padrao do modal de credenciamento). */
.deadline-dialog {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.deadline-dialog__header,
.deadline-dialog__footer {
  flex-shrink: 0;
}

.deadline-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.deadline-dialog__single {
  padding: 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
}

.deadline-dialog__list {
  max-height: 240px;
  overflow-y: auto;
  padding: 4px 8px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
}
</style>
